import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Link, Navigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import ExportReport from '../../Components/Common/ExportReport';
import TableContainer from '../../Components/Common/TableContainer';
import useQsParams from '../../Components/Hooks/QueryString';
import { formatBrazilDate, formatCellphone, formatCurrency, formatDocument } from '../../helpers/format';
import * as api from '../../helpers/bff_helper';
import { useSelector } from 'react-redux';
import ReactSelect from 'react-select';

const Withdrawals = (props) => {
  const { t } = props;
  const { searchParams, setSearchParams, setSearchParamsAsObject } = useQsParams();

  const [isLoading, setIsLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [meta, setMeta] = useState({});

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  const authUser = JSON.parse(sessionStorage.getItem('authUser') ?? '{}');
  const isAdmin =
    !!authUser?.isAdmin ||
    authUser?.role?.name === 'Administrador' ||
    authUser?.role?.name === 'AdministradorOckCash' ||
    authUser?.roles?.some(
      (role) => role?.name === 'Administrador' || role?.name === 'AdministradorOckCash'
    );

  const accountSettings = useSelector((state) => state.GeneralSettings.accountSettings);

  const defaultStatuses = ['solicited', 'approved'];
  const statusParam = searchParams.get('status');
  const selectedStatuses = (statusParam ? statusParam.split(',') : defaultStatuses).filter(Boolean);
  const listFilters = selectedStatuses.length ? { status: { in: selectedStatuses } } : {};

  useEffect(() => {
    if (statusParam) return;
    setSearchParamsAsObject({ status: defaultStatuses.join(','), page: 1 }, false);
  }, []);

  const normalizeWithdrawal = (withdrawal) => {
    const requestedBy = withdrawal?.requestedBy ?? {};
    return {
      ...withdrawal,
      customerName: withdrawal?.customerName ?? requestedBy?.name ?? null,
      customerDocument: withdrawal?.customerDocument ?? requestedBy?.document ?? null,
      customerEmail: withdrawal?.customerEmail ?? requestedBy?.email ?? null,
      customerCellphone: withdrawal?.customerCellphone ?? requestedBy?.cellphone ?? null,
    };
  };

  const statusOptions = useMemo(
    () => ['available', 'solicited', 'approved', 'transferred', 'rejected', 'expired'].map((value) => ({
      value,
      label: t(`withdrawals.status.${value}`),
    })),
    [t],
  );

  const statusSelectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        backgroundColor: 'var(--bs-body-bg)',
        borderColor: 'var(--bs-border-color)',
        boxShadow: 'none',
        minHeight: '38px',
        ':hover': { borderColor: 'var(--bs-border-color)' },
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: 'var(--bs-body-bg, #fff)',
        color: 'var(--bs-body-color, #212529)',
        zIndex: 1056,
      }),
      menuList: (base) => ({
        ...base,
        backgroundColor: 'var(--bs-body-bg, #fff)',
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? 'var(--bs-tertiary-bg, #f8f9fa)' : 'var(--bs-body-bg, #fff)',
        color: 'var(--bs-body-color, #212529)',
        ':active': { backgroundColor: 'var(--bs-tertiary-bg, #f8f9fa)' },
      }),
      placeholder: (base) => ({ ...base, color: 'var(--bs-secondary-color, #6c757d)' }),
      singleValue: (base) => ({ ...base, color: 'var(--bs-body-color, #212529)' }),
      input: (base) => ({ ...base, color: 'var(--bs-body-color, #212529)' }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: 'var(--bs-tertiary-bg, #f8f9fa)',
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: 'var(--bs-body-color, #212529)',
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: 'var(--bs-body-color, #212529)',
        ':hover': {
          backgroundColor: 'var(--bs-danger-bg-subtle, #f8d7da)',
          color: 'var(--bs-danger-text-emphasis, #842029)',
        },
      }),
    }),
    [],
  );

  const statusValue = useMemo(() => {
    const selected = new Set(selectedStatuses);
    return statusOptions.filter((o) => selected.has(o.value));
  }, [selectedStatuses.join(','), statusOptions]);

  const fetchWithdrawals = async () => {
    setIsLoading(true);
    try {
      const page = Number(searchParams.get('page') ?? 1);
      const response = await api.getWithdrawals({
        page,
        take: 10,
        order: 'desc',
        filters: {
          ...listFilters,
        },
      });
      setWithdrawals((response.data || []).map(normalizeWithdrawal));
      setMeta(response.meta || {});
    } catch (error) {
      toast.error(t('withdrawals.messages.fetch-error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchWithdrawals();
  }, [searchParams.toString()]);

  const approve = async (withdrawalId) => {
    try {
      await api.updateWithdrawal(withdrawalId, { action: 'approve' });
      toast.success(t('withdrawals.messages.approved'));
      fetchWithdrawals();
    } catch (error) {
      toast.error(t('withdrawals.messages.update-error'));
    }
  };

  const transfer = async (withdrawalId) => {
    try {
      await api.updateWithdrawal(withdrawalId, { action: 'transfer' });
      toast.success(t('withdrawals.messages.transferred'));
      fetchWithdrawals();
    } catch (error) {
      toast.error(t('withdrawals.messages.update-error'));
    }
  };

  const changeStatusesFilter = (values) => {
    const next = (values ?? []).map((v) => v.value);
    if (!next.length) {
      setSearchParamsAsObject({ page: 1, status: undefined }, false);
      return;
    }
    setSearchParamsAsObject({ page: 1, status: next.join(',') }, false);
  };

  const openRejectModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const reject = async () => {
    if (!selectedWithdrawal) return;
    try {
      await api.updateWithdrawal(selectedWithdrawal.id, { action: 'reject', reason: rejectReason });
      toast.success(t('withdrawals.messages.rejected'));
      setRejectModalOpen(false);
      setSelectedWithdrawal(null);
      setRejectReason('');
      fetchWithdrawals();
    } catch (error) {
      toast.error(t('withdrawals.messages.update-error'));
    }
  };

  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case 'solicited':
        return 'bg-warning-subtle text-warning';
      case 'approved':
        return 'bg-info-subtle text-info';
      case 'transferred':
        return 'bg-success-subtle text-success';
      case 'rejected':
        return 'bg-danger-subtle text-danger';
      case 'expired':
        return 'bg-secondary-subtle text-secondary';
      case 'available':
      default:
        return 'bg-light text-dark';
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: t('withdrawals.table.ticket'),
        Cell: (cellProps) => {
          const orderId = cellProps?.row?.original?.orderId;
          const orderTicket = cellProps?.row?.original?.orderTicket;

          if (!orderTicket) return '-'

          return (
            <Link
            to={`/vendas/${orderId}`}
            style={{ borderBottom: '1px solid lightgray' }}
          >
            {orderTicket}
          </Link>
          )
        },
        reportProps: {
          accessor: 'ticket',
        },
      },
      {
        Header: t('withdrawals.table.customer-name'),
        accessor: 'customerName',
        Cell: (cellProps) => cellProps.row.original.customerName || '-',
      },
      {
        Header: t('withdrawals.table.document'),
        accessor: 'customerDocument',
        Cell: (cellProps) => {
          const document = cellProps.row.original.customerDocument;

          if (!document) return '-'

          return (
            <Link
              to={`/carteiras/detalhes/${document}`}
              style={{ borderBottom: '1px solid lightgray' }}
            >
              {formatDocument(document, accountSettings)}
            </Link>
          )
        },
      },
      {
        Header: t('withdrawals.table.phone'),
        accessor: 'customerCellphone',
        Cell: (cellProps) => {
          const values = cellProps.row.original;
          const cellphone = values?.customerCellphone;

          return (
            <div className="d-flex align-items-center">
              {cellphone ? formatCellphone(cellphone) : '-'}
            </div>
          );
        },
        reportProps: {
          accessor: 'cellphone',
        },
      },
      {
        Header: t('withdrawals.table.email'),
        accessor: 'customerEmail',
        Cell: (cellProps) => cellProps.row.original.customerEmail || '-',
      },
      {
        Header: t('withdrawals.table.status'),
        accessor: 'status',
        Cell: (cellProps) => {
          const status = cellProps.row.original.status;
          if (!status) return '-';
          return (
            <span className={`badge rounded-pill ${getStatusBadgeClasses(status)}`}>
              {t(`withdrawals.status.${status}`)}
            </span>
          );
        },
      },
      {
        Header: t('withdrawals.table.value'),
        accessor: 'value',
        Cell: (cellProps) => formatCurrency(cellProps.row.original.value),
      },
      {
        Header: t('withdrawals.table.requested-at'),
        accessor: 'solicitedAt',
        Cell: (cellProps) => formatBrazilDate(cellProps.row.original.solicitedAt),
      },
      {
        Header: t('withdrawals.table.actions'),
        reportProps: { exclude: true },
        Cell: (cellProps) => {
          const withdrawal = cellProps.row.original;
          const status = withdrawal?.status;
          if (status !== 'solicited' && status !== 'approved') return null;
          return (
            <div className="d-flex gap-2">
              {status === 'solicited' && (
                <button className="btn btn-sm btn-success" onClick={() => approve(withdrawal.id)}>
                  {t('withdrawals.actions.approve')}
                </button>
              )}
              {status === 'approved' && (
                <button className="btn btn-sm btn-primary" onClick={() => transfer(withdrawal.id)}>
                  {t('withdrawals.actions.transfer')}
                </button>
              )}
              <button className="btn btn-sm btn-danger" onClick={() => openRejectModal(withdrawal)}>
                {t('withdrawals.actions.reject')}
              </button>
            </div>
          );
        },
      },
    ],
    [t, accountSettings],
  );

  document.title = `${t('withdrawals.title')} | Opencashback`;

  if (!isAdmin) {
    return <Navigate to="/resultados/vendas" />;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('withdrawals.title')} pageTitle={t('tools')} />

          <Row>
            <Col lg="12">
              <Card>
                <CardHeader>
                  <Row className="g-2 justify-content-between">
                    <Col xs="12" sm="6" md="5" lg="4">
                      <ReactSelect
                        isMulti
                        isClearable
                        classNamePrefix="select"
                        placeholder={t('withdrawals.filters.status-placeholder')}
                        options={statusOptions}
                        value={statusValue}
                        onChange={changeStatusesFilter}
                        isDisabled={isLoading}
                        styles={statusSelectStyles}
                      />
                    </Col>
                    <Col xs="12" sm="6" md="4" lg="3">
                      <ExportReport
                        className="w-100"
                        pages={meta?.totalPages || 1}
                        take={meta?.pageSize || 10}
                        disabled={isLoading || !withdrawals?.length}
                        filename="Relatório OCK: Retiradas"
                        service="withdrawals"
                        columns={columns}
                        filters={listFilters}
                      />
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={withdrawals || []}
                    isLoading={isLoading}
                    activePage={meta?.page || 1}
                    pageSize={meta?.pageSize || 10}
                    totalPages={meta?.totalPages || 1}
                    setPage={(p) => setSearchParams('page', p)}
                    count={meta?.total || 0}
                    t={t}
                    theadClass="table-light"
                    divClass="table-responsive table-card mb-0"
                    tableClass="align-middle table-nowrap"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={rejectModalOpen} toggle={() => setRejectModalOpen(!rejectModalOpen)} centered>
        <ModalHeader toggle={() => setRejectModalOpen(!rejectModalOpen)}>
          {t('withdrawals.reject-modal.title')}
        </ModalHeader>
        <ModalBody>
          <div className="mb-2">{t('withdrawals.reject-modal.reason-label')}</div>
          <textarea
            className="form-control"
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-light" onClick={() => setRejectModalOpen(false)}>
            {t('cancel')}
          </button>
          <button className="btn btn-danger" onClick={reject} disabled={!rejectReason}>
            {t('withdrawals.actions.reject')}
          </button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default withTranslation()(Withdrawals);
